'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, buttonVariants } from '@statura/ui';
import { createClient } from '@/lib/supabase/client';
import { useResolvedAuthUser } from '@/contexts/resolved-auth-user-context';
import {
  SESSION_EXPIRED_ACTION_HEADER,
  SESSION_EXPIRED_ACTION_HEADER_VALUE,
  sessionExpiredLoginHref,
} from '@/lib/auth/session-expired-action';

/**
 * P1-11: Save must not hang, and typed work must not be lost, when the session
 * has expired.
 *
 * Middleware answers a Server Action that has no session with a 401 carrying
 * the `x-statura-session: expired` marker (it never runs the action). This
 * guard sees that response before Next's action client does, keeps the action
 * pending, and shows a notice: sign in again in a new tab (this page and its
 * draft stay put), then choose Save now to send the same request again. Choose
 * Don't save to let the action fail with the session-expired message instead.
 *
 * Only Server Action POSTs with the marker are held; every other fetch passes
 * straight through. Before Save now replays anything it checks that the person
 * who signed in again is the person who typed the work; a different account
 * never receives the held request (shared workstations).
 */

type HeldAction = {
  input: RequestInfo | URL;
  init: RequestInit | undefined;
  expiredResponse: Response;
  resolve: (response: Response) => void;
};

type FetchListener = (held: HeldAction) => void;

const PATCH_KEY = Symbol.for('statura.sessionExpiredActionGuard');

type PatchedWindow = Window & {
  [PATCH_KEY]?: { original: typeof fetch; listeners: Set<FetchListener> };
};

function headerValue(init: RequestInit | undefined, name: string): string | null {
  const headers = init?.headers;
  if (!headers) return null;
  if (headers instanceof Headers) return headers.get(name);
  if (Array.isArray(headers)) {
    const match = headers.find(([key]) => key.toLowerCase() === name.toLowerCase());
    return match ? match[1] : null;
  }
  const record = headers as Record<string, string>;
  const key = Object.keys(record).find((candidate) => candidate.toLowerCase() === name);
  return key ? (record[key] ?? null) : null;
}

/** A Server Action request whose body can be sent again unchanged. */
export function isReplayableServerAction(init: RequestInit | undefined): boolean {
  if ((init?.method ?? 'GET').toUpperCase() !== 'POST') return false;
  if (!headerValue(init, 'next-action')) return false;
  const body = init?.body;
  return (
    body == null ||
    typeof body === 'string' ||
    (typeof FormData !== 'undefined' && body instanceof FormData) ||
    (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) ||
    (typeof Blob !== 'undefined' && body instanceof Blob)
  );
}

export function isSessionExpiredActionResponse(response: Response): boolean {
  return (
    response.status === 401 &&
    response.headers.get(SESSION_EXPIRED_ACTION_HEADER) === SESSION_EXPIRED_ACTION_HEADER_VALUE
  );
}

function installFetchGuard(listener: FetchListener): () => void {
  const target = window as PatchedWindow;
  if (!target[PATCH_KEY]) {
    const original = window.fetch.bind(window);
    const listeners = new Set<FetchListener>();
    target[PATCH_KEY] = { original, listeners };
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const response = await original(input, init);
      if (
        listeners.size === 0 ||
        !isSessionExpiredActionResponse(response) ||
        !isReplayableServerAction(init)
      ) {
        return response;
      }
      return new Promise<Response>((resolve) => {
        const held: HeldAction = { input, init, expiredResponse: response, resolve };
        for (const notify of listeners) notify(held);
      });
    };
  }
  const state = target[PATCH_KEY]!;
  state.listeners.add(listener);
  return () => {
    state.listeners.delete(listener);
  };
}

/** The auth user now signed in on this browser, or null. Throws when it cannot tell. */
async function currentAuthUserId(): Promise<string | null> {
  const { data, error } = await createClient().auth.getUser();
  if (error && !data.user) {
    // "No session" is a normal signed-out answer; anything else is unknown.
    if ((error as { name?: string }).name === 'AuthSessionMissingError') return null;
    throw error;
  }
  return data.user?.id ?? null;
}

/**
 * Fail-closed replay decision. Only an identity that is known on both sides
 * and identical lets the held request be sent again.
 */
export type ReplayDecision = 'replay' | 'still-signed-out' | 'unverified' | 'wrong-account';

export function decideReplay(
  expectedUserId: string | null | undefined,
  signedInUserId: string | null | undefined,
): ReplayDecision {
  if (!signedInUserId) return 'still-signed-out';
  if (!expectedUserId) return 'unverified';
  return signedInUserId === expectedUserId ? 'replay' : 'wrong-account';
}

export function SessionExpiredActionGuard({
  resolveCurrentUserId = currentAuthUserId,
}: {
  /** Test seam; production reads the browser session. */
  resolveCurrentUserId?: () => Promise<string | null>;
} = {}) {
  const resolvedUser = useResolvedAuthUser();
  const [held, setHeld] = useState<HeldAction[]>([]);
  const [retrying, setRetrying] = useState(false);
  const [wrongAccount, setWrongAccount] = useState(false);
  const [identityCheckFailed, setIdentityCheckFailed] = useState(false);
  const [returnPath, setReturnPath] = useState('/');
  const heldRef = useRef<HeldAction[]>([]);
  const heldUserRef = useRef<string | null>(null);
  useEffect(() => {
    heldRef.current = held;
  }, [held]);
  useEffect(() => {
    // Who typed the work: fixed while anything is held, so a sign-in as
    // someone else in another tab cannot redefine it.
    if (held.length === 0) heldUserRef.current = resolvedUser?.id ?? null;
  }, [held.length, resolvedUser?.id]);

  useEffect(
    () =>
      installFetchGuard((action) => {
        setReturnPath(`${window.location.pathname}${window.location.search}`);
        setWrongAccount(false);
        setIdentityCheckFailed(false);
        setHeld((current) => [...current, action]);
      }),
    [],
  );

  const release = useCallback(
    async (send: boolean) => {
      const target = window as PatchedWindow;
      const original = target[PATCH_KEY]?.original ?? window.fetch.bind(window);
      const actions = heldRef.current;
      if (!send) {
        setHeld([]);
        for (const action of actions) action.resolve(action.expiredResponse);
        return;
      }
      setRetrying(true);
      setIdentityCheckFailed(false);
      try {
        let signedInUserId: string | null;
        try {
          signedInUserId = await resolveCurrentUserId();
        } catch {
          // Cannot tell who is signed in: keep the work held and never send it.
          setIdentityCheckFailed(true);
          return;
        }
        const decision = decideReplay(heldUserRef.current, signedInUserId);
        if (decision === 'still-signed-out') {
          // Nobody signed in yet: keep holding; the notice stays up.
          setIdentityCheckFailed(true);
          return;
        }
        if (decision !== 'replay') {
          // A different person, or no known author: never send the work as them.
          setHeld([]);
          for (const action of actions) action.resolve(action.expiredResponse);
          setWrongAccount(true);
          return;
        }
        setHeld([]);
        // One at a time, in the order they were made.
        for (const action of actions) {
          try {
            const response = await original(action.input, action.init);
            if (isSessionExpiredActionResponse(response)) {
              // Still signed out: keep holding it and show the notice again.
              setHeld((current) => [...current, { ...action, expiredResponse: response }]);
              continue;
            }
            action.resolve(response);
          } catch {
            action.resolve(action.expiredResponse);
          }
        }
      } finally {
        setRetrying(false);
      }
    },
    [resolveCurrentUserId],
  );

  if (wrongAccount && held.length === 0) {
    return (
      <div
        role="alert"
        data-testid="session-expired-wrong-account"
        className="fixed inset-x-0 top-0 z-[60] border-b border-status-danger-border bg-status-danger-bg px-4 py-3"
      >
        <div className="mx-auto flex max-w-7xl flex-col gap-3 sm:flex-row sm:items-center">
          <p className="flex-1 text-body text-status-danger-fg">
            <strong>The unsaved changes were not sent.</strong> We could not confirm that the person
            now signed in is the person who made them. Reload the page to continue as the account
            now signed in.
          </p>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => window.location.reload()}
          >
            Reload
          </Button>
        </div>
      </div>
    );
  }

  if (held.length === 0 && !retrying) return null;

  const loginHref = sessionExpiredLoginHref(returnPath);

  return (
    <div
      role="alert"
      data-testid="session-expired-action-notice"
      className="fixed inset-x-0 top-0 z-[60] border-b border-status-warning-border bg-status-warning-bg px-4 py-3"
    >
      <div className="mx-auto flex max-w-7xl flex-col gap-3 sm:flex-row sm:items-center">
        <p className="flex-1 text-body text-status-warning-fg">
          <strong>Your session expired, sign in to continue.</strong> Your changes are still on this
          page and have not been saved. Sign in opens in a new tab; then come back here and choose
          Save now.
          {identityCheckFailed ? (
            <span className="mt-1 block" data-testid="session-expired-not-confirmed">
              We could not confirm you are signed in yet, so nothing was sent. Sign in, then choose
              Save now again.
            </span>
          ) : null}
        </p>
        <div className="flex flex-wrap gap-2">
          <a
            href={loginHref}
            target="_blank"
            rel="noopener noreferrer"
            className={buttonVariants({ variant: 'primary', size: 'sm' })}
          >
            Sign in
          </a>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={retrying || held.length === 0}
            onClick={() => void release(true)}
          >
            {retrying ? 'Saving…' : 'Save now'}
          </Button>
          <Button
            type="button"
            variant="tertiary"
            size="sm"
            disabled={retrying || held.length === 0}
            onClick={() => void release(false)}
          >
            Don&apos;t save
          </Button>
        </div>
      </div>
    </div>
  );
}
